import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SERVICES, setActiveService } from "@/lib/services";
import { Reveal } from "./Reveal";

// Homepage-only focused service booking entry. Renders exactly three
// service cards — Airport Transfer, Point-to-Point, Hourly Chauffeur —
// reusing the same SERVICES catalog and click behavior as ServicesWeOffer.
//
// Internal layout (per approved redesign):
//   1. Service title, centered, immediately below the top border.
//   2. Full owner-provided image (object-fit: contain, letterboxed on the
//      card's black background) filling the middle space.
//   3. Short one-line description near the bottom.
//   4. Divider.
//   5. BOOK THIS SERVICE CTA at the bottom.
//
// The three-column row, outer card border/glow/radius, routes, slugs,
// query parameters, click behavior, hover, `data-testid` and accessibility
// contracts are preserved exactly. Only the internal arrangement changes.

const HOME_SLUGS = ["airport-transfers", "point-to-point", "hourly-chauffeur"];

// Owner-supplied landing hero images. These live in /public/landing/ so we
// never overwrite the canonical /services/*.jpg files used by the Services
// catalog page and Airports fallback.
const HERO_IMG = {
  "airport-transfers": "/landing/airport-hero.jpg",
  "point-to-point": "/landing/point-to-point-hero.jpg",
  "hourly-chauffeur": "/landing/hourly-hero.jpg",
};

// Approved bottom-line descriptions per card. If the SERVICES entry already
// carries the same sentence, we prefer the catalog value for consistency.
const APPROVED_DESC = {
  "airport-transfers":
    "Meet-and-greet, flight tracking, and door-to-door precision for arrivals and departures.",
  "point-to-point": "Straight-line executive transfer between any two addresses.",
  "hourly-chauffeur": "A dedicated chauffeur and vehicle for as long as your day requires.",
};

// Displayed title per card. Preserves the current canonical wording.
const DISPLAY_TITLE = {
  "airport-transfers": "Airport Transfers",
  "point-to-point": "Point-to-Point",
  "hourly-chauffeur": "Hourly Chauffeur",
};

export default function HomeServices() {
  const navigate = useNavigate();

  const openService = (slug) => {
    setActiveService(slug);
    navigate(`/fleet/sedan?service=${slug}`);
  };

  const cards = HOME_SLUGS
    .map((slug) => SERVICES.find((s) => s.slug === slug))
    .filter(Boolean);

  return (
    <section
      id="booking"
      data-testid="home-services-section"
      className="px-3 sm:px-4 pt-10 sm:pt-14"
    >
      <style>{`
        .hs-card {
          transition: transform 260ms ease-out, box-shadow 260ms ease-out, border-color 260ms ease-out;
        }
        .hs-card:hover {
          transform: translateY(-6px);
          border-color: rgba(214,212,0,0.75) !important;
          box-shadow: 0 14px 40px rgba(0,0,0,0.55), 0 0 26px rgba(214,212,0,0.22);
        }
        .hs-card:hover .hs-img img { transform: scale(1.04); }
      `}</style>

      <div className="mx-auto max-w-6xl">
        <Reveal className="text-center">
          {/* Enlarged eyebrow per approved brief: at least 2x the previous
              size at every supported viewport, no wrap, no clip. */}
          <p
            className="font-display uppercase tracking-[0.32em] text-[#d6d400] whitespace-nowrap"
            data-testid="home-services-eyebrow"
            style={{
              fontSize: "clamp(22px, 2.4vw, 34px)",
              letterSpacing: "0.28em",
              lineHeight: 1.1,
              textShadow: "0 0 14px rgba(214,212,0,0.28)",
            }}
          >
            Start Booking
          </p>
          <h2
            className="font-display font-bold uppercase tracking-[0.06em] text-[#f4f4ea] mt-3 sm:mt-4 leading-tight"
            data-testid="home-services-heading"
            style={{ fontSize: "clamp(20px, 3.4vw, 40px)" }}
          >
            Book By Choosing Your Service
          </h2>
        </Reveal>

        {/* Fixed 3-column grid at every width. Aspect-ratio on each card
            keeps the three cards a matched, familiar portrait proportion so
            outer dimensions stay in-family with the approved sizing. */}
        <div
          className="mt-6 sm:mt-10 grid grid-cols-3 gap-2 sm:gap-4 lg:gap-6"
          data-testid="home-services-grid"
        >
          {cards.map((s) => {
            const desc = APPROVED_DESC[s.slug];
            const title = DISPLAY_TITLE[s.slug];
            return (
              <button
                key={s.slug}
                type="button"
                onClick={() => openService(s.slug)}
                data-testid={`home-service-card-${s.slug}`}
                aria-label={`Book ${title}`}
                className="hs-card group relative text-left flex flex-col overflow-hidden cursor-pointer"
                style={{
                  background: "#0a0a0a",
                  border: "1px solid rgba(214,212,0,0.3)",
                  borderRadius: "14px",
                  minWidth: 0,
                }}
              >
                {/* 1) TITLE — centered, immediately below the top border. */}
                <div
                  className="w-full text-center px-2 pt-2 sm:pt-3 lg:pt-4"
                  data-testid={`home-service-title-${s.slug}`}
                >
                  <h3
                    className="font-display font-bold uppercase text-[#f4f4ea] leading-tight tracking-[0.03em]"
                    style={{
                      fontSize: "clamp(10px, 1.35vw, 22px)",
                      letterSpacing: "0.03em",
                    }}
                  >
                    {title}
                  </h3>
                </div>

                {/* 2) FULL IMAGE — object-contain preserves 100% of the
                    original owner-provided canvas. Container aspect ratio
                    2/3 (portrait) is shared by all three cards so they
                    match heights; images with taller-than-2/3 ratios (P2P,
                    Hourly) letterbox on the sides against the black card
                    background. Nothing is cropped from any image. */}
                <div
                  className="hs-img relative w-full mt-1 sm:mt-2"
                  style={{ aspectRatio: "2 / 3", background: "#050505" }}
                >
                  <img
                    src={HERO_IMG[s.slug]}
                    alt={`${title} — luxury service imagery`}
                    loading="lazy"
                    decoding="async"
                    draggable={false}
                    className="absolute inset-0 w-full h-full"
                    style={{
                      objectFit: "contain",
                      objectPosition: "center",
                      transition: "transform 500ms ease-out",
                      transform: "translateZ(0)",
                    }}
                  />
                </div>

                {/* 3 + 4 + 5) DESCRIPTION → DIVIDER → CTA at the bottom.
                    Reserve a fixed 3-line-tall block for the description so
                    all three cards align their divider + CTA even when the
                    text lengths differ. */}
                <div className="w-full px-2 sm:px-4 lg:px-5 pb-2 sm:pb-4 lg:pb-5 pt-1 sm:pt-2">
                  <p
                    className="font-body text-[#c9c9c1] text-center leading-snug"
                    data-testid={`home-service-desc-${s.slug}`}
                    style={{
                      fontSize: "clamp(12px, 0.95vw, 13px)",
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      // Reserve 3 lines of vertical space regardless of
                      // whether the copy fits in 1, 2 or 3 → keeps divider
                      // and CTA row aligned across all three cards.
                      minHeight: "calc(1.35em * 3)",
                    }}
                  >
                    {desc}
                  </p>
                  <div
                    className="mt-2 sm:mt-3 pt-2 sm:pt-3 flex items-center justify-between font-display uppercase tracking-[0.1em] sm:tracking-[0.14em] text-[#d6d400]"
                    style={{
                      fontSize: "clamp(8.5px, 0.85vw, 11px)",
                      borderTop: "1px solid rgba(214,212,0,0.22)",
                    }}
                  >
                    <span className="truncate">
                      <span className="hidden sm:inline">Book This Service</span>
                      <span className="sm:hidden">Book</span>
                    </span>
                    <ArrowRight
                      size={12}
                      strokeWidth={2}
                      className="shrink-0 transition-transform group-hover:translate-x-1"
                    />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
